import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Alert } from '@mui/material'
import './NetsuiteExport.css'
import { useMemo, useState } from 'react'
import axios from 'axios'
import { lineItemType, orderType } from '../types'
import { NetsuiteSearch } from '../helper'
import { useUser } from '../hooks'

export function NetsuiteExport({
  orderId,
  salesOrderNumber,
  open,
  lineItems,
  licenseEmail,
  licensePhone,
  setOpen,
  setOrder,
  quantities,
  enableLineItemShipping,
}: {
  orderId: string
  salesOrderNumber: string | null
  open: boolean
  lineItems: lineItemType[]
  licenseEmail: string | null
  licensePhone: string | null
  quantities: number[] | null
  enableLineItemShipping: boolean
  setOpen: (open: boolean) => void
  setOrder: React.Dispatch<React.SetStateAction<orderType>>
}) {
  const user = useUser()

  const [customerName, setCustomerName] = useState('')
  const [exportLoading, setExportLoading] = useState(false)
  const [exportStatus, setExportStatus] = useState<'success' | 'error' | 'processing' | ''>('')

  const errors = useMemo(
    () => ({
      lineItemLength: lineItems.length === 0,
      licenseEmail: licenseEmail ? !/^\S+@\S+\.\S+$/.test(licenseEmail) : false,
      licensePhone: licensePhone ? !/^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/.test(licensePhone) : false,
      quantities: quantities
        ? quantities.some(
            (quantity: number | null | undefined) => quantity === null || quantity === undefined || quantity < 0,
          )
        : false,
      classes: lineItems.some(lineItem => !lineItem.Class || lineItem.Class === ''),
    }),
    [lineItems, licenseEmail, licensePhone, quantities],
  )

  const handleExport = async () => {
    setExportLoading(true)
    axios
      .post(`/netsuite/${orderId}`, {
        customerName: customerName,
        sharepointLink: `https://imprestechnology.sharepoint.com/sites/${process.env.REACT_APP_SHAREPOINT_SITE}/Order%20Portal%20Uploads/Forms/AllItems.aspx?id=%2Fsites%2F${process.env.REACT_APP_SHAREPOINT_SITE}%2FOrder%20Portal%20Uploads%2F${orderId}`,
        submittedBy: user.email,
        enableLineItemShipping: enableLineItemShipping,
      })
      .then(res => {
        if (res.status === 202) {
          setExportStatus('processing')
        } else {
          setExportStatus('success')
          setOrder(prev => ({ ...prev, SalesOrderNumber: res.data }))
        }
        setOpen(false)
      })
      .catch(err => {
        console.error(err)
        setExportStatus('error')
      })
      .finally(() => {
        setExportLoading(false)
      })
  }

  return (
    <>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md" className="netsuite-export-dialog">
        <DialogTitle>Export to NetSuite</DialogTitle>
        <DialogContent sx={{ paddingTop: '5px !important', gap: '16px', display: 'flex', flexDirection: 'column' }}>
          <NetsuiteSearch table="customer" value={customerName} onChange={value => setCustomerName(value ?? '')} />
          {salesOrderNumber && (
            <Alert severity="info">
              This order already exists in Netsuite: {salesOrderNumber}. The NetSuite record will be updated with the
              new information.
            </Alert>
          )}
          {errors.lineItemLength && <Alert severity="error">This order has no line items.</Alert>}
          {errors.licenseEmail && <Alert severity="error">The license contact email is invalid.</Alert>}
          {errors.licensePhone && <Alert severity="error">The license contact phone number is invalid.</Alert>}
          {errors.quantities && <Alert severity="error">There are lines with invalid quantities.</Alert>}
          {errors.classes && <Alert severity="error">All line items must have a contract number selected.</Alert>}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="primary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExport}
            loading={exportLoading}
            disabled={
              errors.lineItemLength || errors.licenseEmail || errors.licensePhone || errors.quantities || errors.classes
            }
          >
            Export
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={exportStatus === 'success'} autoHideDuration={5000} onClose={() => setExportStatus('')}>
        <Alert severity="success" variant="filled">
          Export to NetSuite was successful
        </Alert>
      </Snackbar>
      <Snackbar open={exportStatus === 'error'}>
        <Alert severity="error" variant="filled" onClose={() => setExportStatus('')}>
          Export to NetSuite failed. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
      <Snackbar
        open={exportStatus === 'processing'}
        onClose={() => setExportStatus('')}
        message="Export to NetSuite is processing. You willi receive an email notification when it is complete."
      />
    </>
  )
}
